import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Navigate, useParams, useNavigate, Link } from 'react-router-dom'
import { CheckCircle2, AlertTriangle, User, Receipt, Trash2 } from 'lucide-react'
import { updateProfile, deleteAccount } from '../../lib/api/auth'
import { isDemoEmail } from '../../lib/demo'
import { useAuth } from '../../store/authStore'
import { PageShell } from '../../components/layout/PageShell'
import { Field, Checkbox } from '../../components/ui/Field'
import { Select } from '../../components/ui/Select'
import { COUNTRIES } from '../../lib/countries'
import { Button } from '../../components/ui/Button'
import { BillingFields, billingValid } from '../billing/BillingFields'
import { BILLING_ENABLED } from '../../lib/platform'
import type { PublicUser } from '../../lib/api/types'

const COUNTRY_OPTIONS = COUNTRIES.map((c) => ({ value: c.code, label: c.name }))

type Form = {
  name: string
  lastname: string
  email: string
  phoneNumber: string
  licensePlate: string
  country: string
  billingType: string
  codiceFiscale: string
  referente: string
  businessName: string
  businessVatId: string
  businessSDI: string
  businessEmail: string
  businessPec: string
  businessAddress: string
  businessHouseNumber: string
  businessCity: string
  businessZipCode: string
  businessRegionProvince: string
  businessCountry: string
  isPublicAdministration: string
  commercialPrivacy: string
}

const blank: Form = {
  name: '', lastname: '', email: '', phoneNumber: '', licensePlate: '', country: 'IT',
  billingType: 'privato', codiceFiscale: '', referente: '',
  businessName: '', businessVatId: '', businessSDI: '', businessEmail: '', businessPec: '', businessAddress: '',
  businessHouseNumber: '', businessCity: '', businessZipCode: '', businessRegionProvince: '',
  businessCountry: 'IT', isPublicAdministration: '', commercialPrivacy: '',
}

// existing accounts have no billingType stored — default to "azienda" when company data is present
const hasCompanyData = (u: PublicUser) => Boolean(u.businessName || u.businessVatId || u.businessAddress || u.businessCity)

function fromUser(u: PublicUser): Form {
  return {
    // some accounts store the email as the name (email/social sign-up) — don't show it as a first name
    name: u.name && !u.name.includes('@') ? u.name : '', lastname: u.lastname ?? '', email: u.email ?? '', phoneNumber: u.phoneNumber ?? '',
    licensePlate: (u.licensePlate ?? '').toUpperCase(), country: u.country || u.businessCountry || 'IT',
    billingType: u.billingType || (hasCompanyData(u) ? 'azienda' : 'privato'),
    codiceFiscale: u.codiceFiscale ?? '', referente: u.referente ?? '',
    businessName: u.businessName ?? '', businessVatId: u.businessVatId ?? '', businessSDI: u.businessSDI ?? '',
    businessEmail: u.businessEmail ?? '', businessPec: u.businessPec ?? '', businessAddress: u.businessAddress ?? '', businessHouseNumber: u.businessHouseNumber ?? '',
    businessCity: u.businessCity ?? '', businessZipCode: u.businessZipCode ?? '', businessRegionProvince: u.businessRegionProvince ?? '',
    businessCountry: u.country || u.businessCountry || 'IT',
    isPublicAdministration: u.isPublicAdministration === true || u.isPublicAdministration === 'true' ? 'true' : '',
    commercialPrivacy: u.commercialPrivacy === true || u.commercialPrivacy === 'true' ? 'true' : '',
  }
}

export function ProfilePage() {
  const { t } = useTranslation()
  const { locale: routeLocale = 'it' } = useParams()
  const status = useAuth((s) => s.status)
  const user = useAuth((s) => s.user)
  const setUser = useAuth((s) => s.setUser)
  const signOut = useAuth((s) => s.signOut)
  const navigate = useNavigate()

  const [form, setForm] = useState<Form>(blank)
  const [loaded, setLoaded] = useState(false)
  const [touched, setTouched] = useState(false)
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState<'saved' | 'error' | 'invalid' | null>(null)
  const [showDelete, setShowDelete] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [deleteErr, setDeleteErr] = useState(false)

  useEffect(() => {
    if (user && !loaded) {
      setForm(fromUser(user))
      setLoaded(true)
    }
  }, [user, loaded])

  if (status === 'unknown')
    return (
      <PageShell narrow desktopWide>
        <div className="gsm-card h-64 animate-pulse" />
      </PageShell>
    )
  if (status === 'guest') return <Navigate to={`/${routeLocale}/login?redirect=/${routeLocale}/profile`} replace />

  const set = (k: keyof Form) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setMsg(null)
    setForm((p) => ({ ...p, [k]: e.target.value }))
  }
  const setBilling = (k: keyof Form, v: string) => {
    setMsg(null)
    setForm((p) => ({ ...p, [k]: v }))
  }
  const scrollToFirstError = () =>
    setTimeout(() => {
      const el = document.querySelector('[aria-invalid="true"]') as HTMLElement | null
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        el.focus({ preventScroll: true })
      }
    }, 60)

  async function onDelete() {
    setDeleting(true)
    setDeleteErr(false)
    try {
      await deleteAccount()
      await signOut()
      navigate(`/${routeLocale}`, { replace: true })
    } catch {
      setDeleteErr(true) // backend endpoint not available → offer the email-request path
    } finally {
      setDeleting(false)
    }
  }

  async function onSave(e: React.FormEvent) {
    e.preventDefault()
    setTouched(true)
    if (isDemoEmail(user?.email)) {
      // demo accounts are client-side only — never write to the backend
      setMsg('saved')
      return
    }
    // Billing hidden (BILLING_ENABLED) → don't block the profile save on billing validity.
    if (BILLING_ENABLED && !billingValid(form)) {
      setMsg('invalid')
      scrollToFirstError()
      return
    }
    setSaving(true)
    setMsg(null)
    try {
      const updated = await updateProfile(form)
      if (updated) {
        setUser(updated)
        setForm(fromUser(updated))
        setMsg('saved')
      } else {
        setMsg('error')
      }
    } catch {
      setMsg('error')
    } finally {
      setSaving(false)
    }
  }

  return (
    <PageShell narrow desktopWide>
      <div className="mb-6">
        <h1 className="font-display text-2xl font-extrabold tracking-tight text-ink-900 sm:text-3xl">{t('profile.title')}</h1>
        <p className="mt-1.5 text-sm text-ink-500">{t('profile.subtitle')}</p>
      </div>

      <form onSubmit={onSave}>
       <fieldset disabled={saving} className="space-y-5">
        {/* personal */}
        <section className="gsm-card p-5 sm:p-6">
          <h2 className="mb-4 flex items-center gap-2 font-display text-sm font-bold text-ink-900">
            <User className="h-4 w-4 text-brand-600" /> {t('profile.personal')}
          </h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <Field label={t('info.firstName')} value={form.name} onChange={set('name')} autoComplete="given-name" />
            <Field label={t('info.lastName')} value={form.lastname} onChange={set('lastname')} autoComplete="family-name" />
            <Field label={t('info.email')} type="email" value={form.email} onChange={set('email')} autoComplete="email" />
            <Field label={t('info.phone')} value={form.phoneNumber} onChange={set('phoneNumber')} autoComplete="tel" />
            <Field label={t('info.plate')} value={form.licensePlate} onChange={(e) => { setMsg(null); setForm((p) => ({ ...p, licensePlate: e.target.value.toUpperCase() })) }} />
            <div>
              <label className="gsm-label">{t('info.country')}</label>
              <Select
                value={form.country || 'IT'}
                options={COUNTRY_OPTIONS}
                onChange={(v) => { setMsg(null); setForm((p) => ({ ...p, country: v, businessCountry: v })) }}
                searchable
                ariaLabel={t('info.country')}
              />
            </div>
          </div>
          <div className="mt-4 border-t border-ink-100 pt-3">
            <Checkbox
              checked={form.commercialPrivacy === 'true'}
              onChange={(v) => { setMsg(null); setForm((p) => ({ ...p, commercialPrivacy: v ? 'true' : '' })) }}
              label={t('info.marketing')}
            />
          </div>
        </section>

        {/* billing — TEMPORARILY HIDDEN via BILLING_ENABLED (set VITE_BILLING_ENABLED=true to restore) */}
        {BILLING_ENABLED && (
          <section className="gsm-card p-5 sm:p-6">
            <h2 className="mb-1 flex items-center gap-2 font-display text-sm font-bold text-ink-900">
              <Receipt className="h-4 w-4 text-brand-600" /> {t('profile.billing')}
            </h2>
            <p className="mb-4 text-xs text-ink-400">{t('profile.billingHint')}</p>
            <BillingFields value={form} set={setBilling} touched={touched} requiredLabel={t('common.required')} />
            <div className="mt-4 border-t border-ink-100 pt-3">
              <Checkbox
                checked={form.isPublicAdministration === 'true'}
                onChange={(v) => { setMsg(null); setForm((p) => ({ ...p, isPublicAdministration: v ? 'true' : '' })) }}
                label={t('payment.pa')}
              />
            </div>
          </section>
        )}

        {msg === 'saved' && (
          <p className="flex items-center gap-2 rounded-xl bg-green-50 px-4 py-2.5 text-sm font-medium text-green-700">
            <CheckCircle2 className="h-4 w-4" /> {t('profile.saved')}
          </p>
        )}
        {msg === 'invalid' && (
          <p className="flex items-center gap-2 rounded-xl bg-brand-50 px-4 py-2.5 text-sm font-medium text-brand-700">
            <AlertTriangle className="h-4 w-4" /> {t('profile.invalidBilling')}
          </p>
        )}
        {msg === 'error' && (
          <p className="flex items-center gap-2 rounded-xl bg-brand-50 px-4 py-2.5 text-sm font-medium text-brand-700">
            <AlertTriangle className="h-4 w-4" /> {t('profile.saveError')}
          </p>
        )}

        <div className="lg:flex lg:justify-end">
          <Button type="submit" size="lg" className="w-full lg:w-auto lg:px-12" loading={saving}>
            {t('profile.save')}
          </Button>
        </div>
       </fieldset>
      </form>

      <section className="mt-6 rounded-2xl bg-white p-5 shadow-card ring-1 ring-brand-100 sm:p-6">
        <h2 className="font-display text-lg font-bold text-ink-900">{t('profile.deleteTitle')}</h2>
        <p className="mt-1.5 max-w-prose text-sm text-ink-500">
          {t('profile.deleteBodyPre')}{' '}
          <Link to={`/${routeLocale}/data-deletion`} className="font-medium text-brand-600 underline">{t('common.moreInfo')}</Link>.
        </p>
        <button
          type="button"
          onClick={() => {
            setShowDelete(true)
            setDeleteErr(false)
          }}
          className="mt-4 inline-flex items-center gap-2 rounded-xl border border-brand-200 px-4 py-2.5 text-sm font-semibold text-brand-700 transition hover:bg-brand-50"
        >
          <Trash2 className="h-4 w-4" /> {t('profile.deleteButton')}
        </button>
      </section>

      {showDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-pop">
            <h3 className="font-display text-lg font-bold text-ink-900">{t('profile.deleteModalTitle')}</h3>
            <p className="mt-2 text-sm text-ink-500">
              {t('profile.deleteModalText')}
            </p>
            {deleteErr && (
              <p className="mt-3 rounded-xl bg-brand-50 px-3 py-2 text-[13px] leading-relaxed text-brand-700">
                {t('profile.deleteErrorPre')}{' '}
                <a href="mailto:privacy@garagesanmarco.it" className="underline">privacy@garagesanmarco.it</a> {t('profile.deleteErrorMid')}{' '}
                <Link to={`/${routeLocale}/data-deletion`} className="underline">{t('profile.deleteErrorPage')}</Link>.
              </p>
            )}
            <div className="mt-5 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowDelete(false)}
                className="rounded-xl border border-ink-200 px-4 py-2 text-sm font-semibold text-ink-600 hover:bg-ink-50"
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                onClick={onDelete}
                disabled={deleting}
                className="rounded-xl bg-brand-600 px-4 py-2 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
              >
                {deleting ? t('profile.deleting') : t('profile.deleteConfirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </PageShell>
  )
}
